import React, { useState } from "react";

import { faBolt, faPlus } from "@fortawesome/pro-regular-svg-icons";

import { EmptyState } from "src/design-system/EmptyState";
import { useModal } from "src/design-system/Modal";
import { SubHeader, SubHeaderFilter } from "src/flow/SubHeader";
import { useCapabilities } from "src/hooks/useCapabilities";
import { flowFolderScope } from "src/hooks/useCapabilities/flowFolderScope";
import { useJobsWithVersionsUsed } from "src/jobs/api/queries";
import { ManageJobModal } from "src/jobs/common/ManageJobModal";
import { WhitePane } from "src/jobs/common/WhitePane";
import { JobsTable } from "src/jobs/JobsTable";
import { JobStatus, JobWithVersionsUsed } from "src/jobs/types";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { useFlowContext } from "src/router/routerContextHooks";
import { useIsEngineerOrAdmin } from "src/store/AuthStore";

export const JobsContent: React.FC = () => {
  const { workspace, flow } = useFlowContext();
  const [statusFilter, setStatusFilter] = useState<Nullable<JobStatus>>(null);
  const isEngineerOrAdmin = useIsEngineerOrAdmin();
  const { data, isLoading } = useJobsWithVersionsUsed({
    baseUrl: workspace.base_url!,
    flow: flow,
    status: statusFilter,
    includeTestRuns:
      isEngineerOrAdmin && isFeatureFlagEnabled(FEATURE_FLAGS.powertools),
  });

  const thereAreNoJobs = !isLoading && data?.length === 0;

  const { isOpen, openModal, closeModal } = useModal();

  const { jobs } = useCapabilities();

  return (
    <>
      <SubHeader title="Jobs" paddedParent>
        <SubHeaderFilter
          options={[
            { key: "active", value: "Active" },
            { key: "inactive", value: "Inactive" },
          ]}
          selected={statusFilter}
          onChange={setStatusFilter}
        />
        {jobs.canCreate(flowFolderScope(flow)) && (
          <SubHeader.Button
            icon={faPlus}
            tooltip="Create Job"
            onClick={openModal}
          />
        )}
      </SubHeader>
      <div
        className="mx-auto flex w-full max-w-[1800px] flex-col gap-y-5.5"
        data-loc="jobs-content"
      >
        <div className="flex justify-between">
          <WhitePane>
            {thereAreNoJobs ? (
              <JobsEmptyState
                openModal={openModal}
                statusFilter={statusFilter}
              />
            ) : (
              <JobsTable
                data={(data as JobWithVersionsUsed[]) ?? []}
                isLoading={isLoading}
              />
            )}
          </WhitePane>
        </div>
      </div>
      <ManageJobModal action="create" open={isOpen} onClose={closeModal} />
    </>
  );
};

const JobsEmptyState: React.FC<{
  statusFilter: Nullable<JobStatus>;
  openModal: () => void;
}> = ({ statusFilter, openModal }) => {
  if (statusFilter === null) {
    return (
      <EmptyState
        actionButton={{
          icon: faPlus,
          label: "Create a Job",
          onClick: openModal,
        }}
        dataLoc="no-jobs"
        description="You can add jobs associated with this Decision Flow. The Job can be manually run, or can be setup to automatically run based on a schedule."
        headline="No jobs added"
        icon={faBolt}
      />
    );
  }

  return (
    <EmptyState
      description="No records match your current filter criteria. Try to adjust your filter settings or reset the filter."
      headline="No jobs found"
      icon={faBolt}
    />
  );
};
